// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import StripePlan from "./stripe-plan.js"

const modelClassData = {
  "attributes": {
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "quantity": {
      "column": {
        "default": null,
        "name": "quantity",
        "null": true,
        "type": "integer"
      },
      "name": "quantity",
      "selected_by_default": null,
      "translated": null
    },
    "stripe_plan_id": {
      "column": {
        "default": null,
        "name": "stripe_plan_id",
        "null": true,
        "type": "string"
      },
      "name": "stripe_plan_id",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "StripeSubscriptionItem",
  "collectionKey": "stripe_subscription_items",
  "collectionName": "stripe_subscription_items",
  "i18nKey": "stripe_subscription_item",
  "camelizedLower": "stripeSubscriptionItem",
  "name": "StripeSubscriptionItem",
  "nameDasherized": "stripe-subscription-item",
  "pluralName": "stripe_subscription_items",
  "ransackable_associations": [
    {
      "className": "ActiveRecordAuditable::Audit",
      "collectionName": "audits",
      "foreignKey": "auditable_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "Audit",
      "through": null
    },
    {
      "className": "ActiveRecordAuditable::Audit",
      "collectionName": "audits",
      "foreignKey": "auditable_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "Audit",
      "through": null
    },
    {
      "className": "StripeInvoiceItem",
      "collectionName": "stripe_invoice_items",
      "foreignKey": "stripe_subscription_item_id",
      "name": "stripe_invoice_items",
      "macro": "has_many",
      "resource_name": "StripeInvoiceItem",
      "through": null
    },
    {
      "className": "StripePlan",
      "collectionName": "stripe_plans",
      "foreignKey": "stripe_plan_id",
      "name": "stripe_plan",
      "macro": "belongs_to",
      "resource_name": "StripePlan",
      "through": null
    },
    {
      "className": "StripePrice",
      "collectionName": null,
      "foreignKey": "stripe_price_id",
      "name": "stripe_price",
      "macro": "belongs_to",
      "resource_name": null,
      "through": null
    },
    {
      "className": "StripeSubscription",
      "collectionName": "stripe_subscriptions",
      "foreignKey": "stripe_subscription_id",
      "name": "stripe_subscription",
      "macro": "belongs_to",
      "resource_name": "StripeSubscription",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created",
      "column": {
        "default": null,
        "name": "created",
        "null": true,
        "type": "datetime"
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "deleted",
      "column": {
        "default": "0",
        "name": "deleted",
        "null": false,
        "type": "boolean"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "metadata",
      "column": {
        "default": null,
        "name": "metadata",
        "null": true,
        "type": "text"
      }
    },
    {
      "name": "quantity",
      "column": {
        "default": null,
        "name": "quantity",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "stripe_id",
      "column": {
        "default": null,
        "name": "stripe_id",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "stripe_plan_id",
      "column": {
        "default": null,
        "name": "stripe_plan_id",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "stripe_price_id",
      "column": {
        "default": null,
        "name": "stripe_price_id",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "stripe_subscription_id",
      "column": {
        "default": null,
        "name": "stripe_subscription_id",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "StripePlan",
      "collectionName": "stripe_plans",
      "foreignKey": "stripe_plan_id",
      "name": "stripe_plan",
      "macro": "belongs_to",
      "resource_name": "StripePlan",
      "through": null
    }
  ],
  "paramKey": "stripe_subscription_item",
  "primaryKey": "id"
}

/** Frontend model for StripeSubscriptionItem. */
export default class StripeSubscriptionItem extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {number | null} */
  quantity() {
    return this.readAttributeUnderscore("quantity")
  }

  /** @returns {boolean} */
  hasQuantity() {
    const value = this.quantity()

    return this._isPresent(value)
  }

  /** @returns {string | null} */
  stripePlanId() {
    return this.readAttributeUnderscore("stripe_plan_id")
  }

  /** @returns {boolean} */
  hasStripePlanId() {
    const value = this.stripePlanId()

    return this._isPresent(value)
  }

  /** @returns {import("./stripe-plan.js").default | null} */
  stripePlan() {
    return this._readBelongsToReflection({modelClass: StripePlan, reflectionName: "stripe_plan"})
  }

  /** @returns {Promise<import("./stripe-plan.js").default | null>} */
  loadStripePlan() {
    if (!("stripePlanId" in this)) throw new Error("Foreign key method wasn't defined: stripePlanId")

    const id = this.stripePlanId()
    const ransack = {}

    ransack["stripe_id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "stripe_plan",
        model: this,
        modelClass: StripePlan
      },
      {ransack}
    )
  }
}
